/**
 * Centralised validation library.
 *
 * Usage:
 *   import { validate, rules } from '../lib/validation'
 *
 *   // One-shot
 *   const err = validate('9876543210', [rules.phone()])   // '' | error string
 *
 *   // Compose
 *   const err = validate(value, [rules.required(), rules.minLength(3)])
 */

// ── Rule type ─────────────────────────────────────────────────────────────────

export interface ValidationRule {
  test: (value: string) => boolean
  message: string
}

// ── Runner ────────────────────────────────────────────────────────────────────

/**
 * Runs rules in order, returns the first failing message or '' if all pass.
 */
export function validate(value: string, ruleList: ValidationRule[]): string {
  const trimmed = value?.trim() ?? ''
  for (const rule of ruleList) {
    if (!rule.test(trimmed)) return rule.message
  }
  return ''
}

// ── Built-in rules ────────────────────────────────────────────────────────────

export const rules = {
  // ── Presence ────────────────────────────────────────────────────────────────

  required: (message = 'This field is required'): ValidationRule => ({
    test: v => v.length > 0,
    message,
  }),

  // ── Length ──────────────────────────────────────────────────────────────────

  minLength: (min: number, message?: string): ValidationRule => ({
    test: v => v.length === 0 || v.length >= min,
    message: message ?? `Must be at least ${min} characters`,
  }),

  maxLength: (max: number, message?: string): ValidationRule => ({
    test: v => v.length <= max,
    message: message ?? `Must be ${max} characters or fewer`,
  }),

  exactLength: (len: number, message?: string): ValidationRule => ({
    test: v => v.length === 0 || v.length === len,
    message: message ?? `Must be exactly ${len} characters`,
  }),

  // ── Name ────────────────────────────────────────────────────────────────────

  /** Full/display name — letters, spaces, dots, hyphens, apostrophes only */
  name: (message = 'Enter a valid name (letters, spaces, - . only)'): ValidationRule => ({
    test: v => v.length === 0 || /^[a-zA-ZÀ-ɏ\s.\-']+$/.test(v),
    message,
  }),

  nameMinLength: (min = 2, message?: string): ValidationRule => ({
    test: v => v.length === 0 || v.replace(/\s/g, '').length >= min,
    message: message ?? `Name must be at least ${min} characters`,
  }),

  nameMaxLength: (max = 100, message?: string): ValidationRule => ({
    test: v => v.length <= max,
    message: message ?? `Name must be ${max} characters or fewer`,
  }),

  // ── Contact ─────────────────────────────────────────────────────────────────

  /** Indian 10-digit mobile, starts with 6-9 */
  phone: (message = 'Enter a valid 10-digit mobile number (starts with 6-9)'): ValidationRule => ({
    test: v => v.length === 0 || /^[6-9]\d{9}$/.test(v.replace(/[\s\-().+]/g, '')),
    message,
  }),

  /** Generic international phone — 7–15 digits, optional + prefix */
  phoneIntl: (message = 'Enter a valid phone number'): ValidationRule => ({
    test: v => v.length === 0 || /^\+?[\d\s\-().]{7,20}$/.test(v),
    message,
  }),

  email: (message = 'Enter a valid email address'): ValidationRule => ({
    test: v => v.length === 0 || /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v),
    message,
  }),

  // ── Identity / Tax (India) ──────────────────────────────────────────────────

  /** 6-digit Indian PIN code */
  pincode: (message = 'Enter a valid 6-digit PIN code'): ValidationRule => ({
    test: v => v.length === 0 || /^[1-9]\d{5}$/.test(v),
    message,
  }),

  /** PAN — AAAAA9999A */
  pan: (message = 'Enter a valid PAN (e.g. ABCDE1234F)'): ValidationRule => ({
    test: v => v.length === 0 || /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(v.toUpperCase()),
    message,
  }),

  /** Aadhaar — 12 digits, not starting with 0 or 1 */
  aadhar: (message = 'Enter a valid 12-digit Aadhaar number'): ValidationRule => ({
    test: v => {
      const digits = v.replace(/\s/g, '')
      return digits.length === 0 || (/^[2-9]\d{11}$/.test(digits))
    },
    message,
  }),

  /**
   * GST Identification Number
   * Format: 2-digit state code + 10-char PAN + 1 entity number + Z + checksum
   * e.g. 27AAPFU0939F1ZV
   */
  gstin: (message = 'Enter a valid 15-character GSTIN'): ValidationRule => ({
    test: v =>
      v.length === 0 ||
      /^\d{2}[A-Z]{5}\d{4}[A-Z]\d[Z][A-Z\d]$/.test(v.toUpperCase()),
    message,
  }),

  /** IFSC code — 4 letters + 0 + 6 alphanumerics */
  ifsc: (message = 'Enter a valid IFSC code (e.g. SBIN0001234)'): ValidationRule => ({
    test: v => v.length === 0 || /^[A-Z]{4}0[A-Z0-9]{6}$/.test(v.toUpperCase()),
    message,
  }),

  // ── Numbers ─────────────────────────────────────────────────────────────────

  numeric: (message = 'Must contain numbers only'): ValidationRule => ({
    test: v => v.length === 0 || /^\d+$/.test(v),
    message,
  }),

  decimal: (message = 'Must be a valid number'): ValidationRule => ({
    test: v => v.length === 0 || /^-?\d+(\.\d+)?$/.test(v),
    message,
  }),

  min: (minVal: number, message?: string): ValidationRule => ({
    test: v => v.length === 0 || Number(v) >= minVal,
    message: message ?? `Must be at least ${minVal}`,
  }),

  max: (maxVal: number, message?: string): ValidationRule => ({
    test: v => v.length === 0 || Number(v) <= maxVal,
    message: message ?? `Must be ${maxVal} or less`,
  }),

  positive: (message = 'Must be a positive number'): ValidationRule => ({
    test: v => v.length === 0 || Number(v) > 0,
    message,
  }),

  // ── Web ─────────────────────────────────────────────────────────────────────

  url: (message = 'Enter a valid URL (e.g. https://example.com)'): ValidationRule => ({
    test: v => {
      if (v.length === 0) return true
      try { new URL(v.startsWith('http') ? v : `https://${v}`); return true }
      catch { return false }
    },
    message,
  }),

  // ── Password ─────────────────────────────────────────────────────────────────

  passwordStrength: (message = 'Password must be 8+ chars with uppercase, lowercase, number & symbol'): ValidationRule => ({
    test: v =>
      v.length === 0 ||
      (/[A-Z]/.test(v) && /[a-z]/.test(v) && /\d/.test(v) && /[^A-Za-z0-9]/.test(v) && v.length >= 8),
    message,
  }),

  noSpaces: (message = 'Must not contain spaces'): ValidationRule => ({
    test: v => !/ /.test(v),
    message,
  }),

  // ── Match ────────────────────────────────────────────────────────────────────

  matches: (other: string, message = 'Values do not match'): ValidationRule => ({
    test: v => v.length === 0 || v === other,
    message,
  }),

  // ── Pattern ──────────────────────────────────────────────────────────────────

  pattern: (regex: RegExp, message: string): ValidationRule => ({
    test: v => v.length === 0 || regex.test(v),
    message,
  }),
}

// ── Pre-built field rule sets ─────────────────────────────────────────────────

/**
 * Ready-made rule arrays for common field types.
 * Pass `required: true` to prepend the required rule.
 */
export const fieldRules = {
  name: (opts: { required?: boolean; min?: number; max?: number } = {}) => [
    ...(opts.required ? [rules.required('Name is required')] : []),
    rules.nameMinLength(opts.min ?? 2),
    rules.nameMaxLength(opts.max ?? 100),
    rules.name(),
  ],

  phone: (opts: { required?: boolean } = {}) => [
    ...(opts.required ? [rules.required('Phone number is required')] : []),
    rules.phone(),
  ],

  email: (opts: { required?: boolean } = {}) => [
    ...(opts.required ? [rules.required('Email is required')] : []),
    rules.email(),
  ],

  password: (opts: { required?: boolean } = {}) => [
    ...(opts.required ? [rules.required('Password is required')] : []),
    rules.minLength(8, 'Password must be at least 8 characters'),
    rules.passwordStrength(),
  ],

  pincode: (opts: { required?: boolean } = {}) => [
    ...(opts.required ? [rules.required('PIN code is required')] : []),
    rules.numeric('PIN code must contain digits only'),
    rules.exactLength(6, 'PIN code must be exactly 6 digits'),
    rules.pincode(),
  ],

  pan: (opts: { required?: boolean } = {}) => [
    ...(opts.required ? [rules.required('PAN is required')] : []),
    rules.exactLength(10, 'PAN must be exactly 10 characters'),
    rules.pan(),
  ],

  aadhar: (opts: { required?: boolean } = {}) => [
    ...(opts.required ? [rules.required('Aadhaar number is required')] : []),
    rules.aadhar(),
  ],

  gstin: (opts: { required?: boolean } = {}) => [
    ...(opts.required ? [rules.required('GSTIN is required')] : []),
    rules.exactLength(15, 'GSTIN must be exactly 15 characters'),
    rules.gstin(),
  ],

  ifsc: (opts: { required?: boolean } = {}) => [
    ...(opts.required ? [rules.required('IFSC code is required')] : []),
    rules.exactLength(11, 'IFSC must be exactly 11 characters'),
    rules.ifsc(),
  ],

  url: (opts: { required?: boolean } = {}) => [
    ...(opts.required ? [rules.required('URL is required')] : []),
    rules.url(),
  ],
}
